/**
 * 🏆 WorldBossLeaderboard — live top-10 damage board + projected trophy
 * preview card for the Arena page.
 *
 * Rendered inside every active world-boss row. Pulls data straight from
 * `/api/arena/world-boss/states` (which the Arena already polls every
 * ~5s) via the `state` prop — no additional HTTP cost.
 *
 * Each row shows: rank · name · damage dealt · damage share · projected
 * reward (nests / chits) at kill time. The viewer's own row is pinned
 * highlighted so a grinder can see their rank + payout at a glance.
 */
import { useAuth } from "@/contexts/AuthContext";

/** Pretty-print the projected reward map ({nests, chits} → emoji string). */
function rewardLabel(reward) {
  if (!reward || typeof reward !== "object") return "—";
  const bits = [];
  if (reward.nests) bits.push(`🪺 ${Number(reward.nests).toLocaleString()}`);
  if (reward.chits) bits.push(`🪙 ${Number(reward.chits).toLocaleString()}`);
  return bits.length ? bits.join(" · ") : "—";
}

export default function WorldBossLeaderboard({ bossId, state }) {
  const { user } = useAuth();
  const myId = user?.user_id;
  const contribs = Array.isArray(state?.top_contributors) ? state.top_contributors : [];
  const killingBlowBy = state?.killing_blow_by;
  const pool = state?.projection_pool || {};
  const kbBonus = state?.projection_killing_blow || {};

  // Pin the viewer below the top-10 if they didn't crack the list, so
  // they still see where they stand.
  const myRow = contribs.find((c) => c.user_id === myId);

  return (
    <div
      className="border border-amber-500/40 bg-amber-950/15 mt-2 p-2 leading-tight"
      data-testid={`arena-world-boss-leaderboard-${bossId}`}
    >
      <div className="flex items-center justify-between text-[9px] uppercase tracking-widest text-amber-300 font-bold mb-1">
        <span>🏆 Live Trophy Preview</span>
        <span className="text-amber-200">
          Pool: {rewardLabel(pool)}
          {(kbBonus.nests || kbBonus.chits) ? (
            <span className="text-amber-400"> · 🐉 KB +{rewardLabel(kbBonus)}</span>
          ) : null}
        </span>
      </div>
      {contribs.length === 0 ? (
        <div className="text-[10px] text-neutral-500 py-1 text-center italic">
          No damage dealt yet. First hit lands rank #1.
        </div>
      ) : (
        <div className="space-y-0.5">
          {contribs.map((c) => {
            const isMe = c.user_id === myId;
            const isKb = c.user_id === killingBlowBy;
            const name = c.display || c.user_id?.slice(0, 10) || "player";
            return (
              <div
                key={c.user_id}
                data-testid={`arena-wb-row-${bossId}-${c.rank}`}
                className={
                  "flex items-center justify-between gap-1 text-[10px] font-mono tabular-nums px-1 py-0.5 " +
                  (isMe
                    ? "bg-amber-500/20 border border-amber-300/60 text-amber-100 font-bold"
                    : isKb
                      ? "bg-rose-900/30 border border-rose-500/40 text-rose-200"
                      : "text-neutral-300")
                }
              >
                <span className="shrink-0 text-center w-5">
                  {c.rank === 1 ? "🥇" : c.rank === 2 ? "🥈" : c.rank === 3 ? "🥉" : `#${c.rank}`}
                </span>
                <span className="flex-1 min-w-0 truncate font-sans text-[10px] uppercase tracking-wider">
                  {name}
                  {isKb && <span className="ml-1 text-rose-300" title="Killing blow">🐉</span>}
                  {isMe && <span className="ml-1 text-amber-200" title="You">(you)</span>}
                </span>
                <span className="shrink-0 text-right text-[10px] text-neutral-400 w-10">
                  {Number(c.damage || 0).toLocaleString()}
                </span>
                <span
                  className="shrink-0 text-right text-[10px] text-amber-200 whitespace-nowrap"
                  title={`Projected trophy: ${rewardLabel(c.projected_reward)}`}
                >
                  {rewardLabel(c.projected_reward)}
                </span>
              </div>
            );
          })}
          {/* If the viewer didn't land in the top-10, hint it. */}
          {myId && !myRow && (
            <div
              data-testid={`arena-wb-row-${bossId}-me-outside`}
              className="text-[10px] text-neutral-500 italic text-center py-0.5"
            >
              You're not in the top 10 yet — top up your damage to crack the trophy list.
            </div>
          )}
        </div>
      )}
      {state?.killing_blow_by && (
        <div className="text-[9px] text-rose-300 uppercase tracking-widest font-bold mt-1 text-center">
          🐉 Killing blow currently belongs to {contribs.find((c) => c.user_id === state.killing_blow_by)?.display || "a raider"}
        </div>
      )}
    </div>
  );
}
